/** What the docked palettes show. AutoCAD's Properties and Layer Properties Manager.
 *
 * ⚠⚠ **THE CHROME ASKS FOR THESE THROUGH `ctx.panels`, WHICH IS A REF AND MUST BE.** See
 * `chrome/shell-types.ts`: the shell's hook runs ABOVE the point where these can be defined, so
 * it is handed a ref that is filled afterwards and read later still, when the palette's own
 * nodes render. Passing the functions directly reads `undefined` on the render that matters.
 *
 * ⚠ Properties shows the DRAWING when nothing is selected, which is AutoCAD's behaviour and not
 * a placeholder — "No selection" over an empty box is a panel that is only ever useful half the
 * time.
 */
import { useEffect, useState } from "react";

import { LINEWEIGHTS, LTYPE_DASH } from "../chrome/acadtables";
import { aciToCss, rgbToCss } from "./aci";
import type { DisplayLayer, DisplayList, Pick } from "./Canvas2D";
import type { DrawingRow, LayerRow } from "./api";

const L: React.CSSProperties = { color: "#9aa0a6", padding: "2px 8px 2px 0",
                                 whiteSpace: "nowrap", verticalAlign: "top" };
const V: React.CSSProperties = { color: "#e8e8e8", padding: "2px 0", wordBreak: "break-word" };

function Section({ title }: { title: string }) {
  return (
    <tr><td colSpan={2} style={{ background: "#4a4d4f", color: "#d0d4d8", padding: "2px 6px",
                                 font: "600 10.5px system-ui", letterSpacing: .3 }}>{title}</td></tr>
  );
}
const row = (k: string, v: React.ReactNode) => (
  <tr key={k}><td style={L}>{k}</td><td style={V}>{v}</td></tr>
);

/** A colour swatch beside its name — AutoCAD shows both, because "173" means nothing on its own
 *  and a swatch alone cannot be typed into a script. */
function Swatch({ aci, rgb, layerAci }: { aci: number; rgb: number | null; layerAci: number }) {
  const css = aciToCss(aci, layerAci, rgb, true);
  const name = aci === 256 ? "ByLayer" : aci === 0 ? "ByBlock" : String(aci);
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span style={{ width: 11, height: 11, background: css, border: "1px solid #777",
                     display: "inline-block" }} />
      {name}
    </span>
  );
}

/** ⚠⚠ **AutoCAD PRINTS `*VARIES*`, NOT A BLANK AND NOT THE FIRST VALUE.** With five objects
 *  selected on three layers, the Layer row says `*VARIES*` — and it stays EDITABLE, because
 *  typing into it sets all five. Showing the first object's layer instead is the dangerous
 *  version: it reads as "these are all on A-WALL" and a drafter acts on it. */
const VARIES = "*VARIES*";
function common<T>(sel: Pick[], f: (p: Pick) => T): T | typeof VARIES | null {
  if (!sel.length) return null;
  const first = f(sel[0]);
  return sel.every((p) => f(p) === first) ? first : VARIES;
}
const varies = (v: unknown) =>
  v === VARIES ? <em style={{ color: "#c8a35a", fontStyle: "normal" }}>{VARIES}</em> : null;

/** The `images` row belonging to a picked UNDERLAY, or null. ⚠ Matched by OWNER HANDLE and by
 *  KIND, never by file name: an IMAGE attached from a file called `plan.pdf` is not an underlay
 *  and must not get an underlay's fields. */
function underlayOf(pick: Pick, dl: DisplayList) {
  if (!pick.kind.endsWith("UNDERLAY")) return null;
  return (dl.images ?? []).find((r) => r[11] === pick.handle
                                       && (r[14] || "").endsWith("UNDERLAY")) ?? null;
}

/** The fade, as AutoCAD's own 0–90 slider. ⚠ Committed on release rather than on every drag
 *  step: each change is a server edit and an undo entry, and a drag would write forty. */
function FadeCell({ value, onSet }: { value: number; onSet: (v: number) => void }) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  return (
    <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
      <input type="range" min={0} max={90} step={5} value={v} data-testid="prop-ul-fade"
             onChange={(e) => setV(Number(e.target.value))}
             onMouseUp={() => v !== value && onSet(v)}
             onKeyUp={(e) => e.key === "Enter" && v !== value && onSet(v)}
             style={{ flex: 1 }} />
      <span style={{ width: 34, textAlign: "right" }}>{v}%</span>
    </span>);
}

export function Properties({ sel, dl, drawing, onSet, onAttr, onUnderlay }: {
  sel: Pick[];
  dl: DisplayList | null;
  drawing: DrawingRow | null;
  /** Write a property onto the whole selection. ⚠⚠ **THE PALETTE IS AN EDITOR, NOT A READOUT.**
   *  AutoCAD's Properties palette is where a drafter moves forty objects onto the right layer,
   *  and this one showed the layer and could not change it — so the only way to fix a
   *  misplaced wall was to erase and redraw it. `undefined` leaves every field read-only,
   *  which is what a host with no write path should get rather than controls that do nothing. */
  onSet?: (patch: Record<string, unknown>) => void;
  /** Write attribute VALUES onto the selected block references, by tag. ⚠ A separate path from
   *  `onSet`: an ATTRIB is a SUB-ENTITY of the INSERT, not a property of it, so the server op is
   *  ATTEDIT and not SETPROP. Merging them would make one of the two lie about what it changed. */
  onAttr?: (values: Record<string, string>) => void;
  /** Write a property onto the selected UNDERLAY. ⚠ A third path beside `onSet` and `onAttr`,
   *  because fade, monochrome and the clip are `PDFUNDERLAY` groups 282/280/11 and the server
   *  op is `underlay`, not SETPROP. Merging them would make one of the two lie. */
  onUnderlay?: (spec: Record<string, unknown>) => void;
}) {
  if (!dl) {
    return <div style={{ padding: 10, color: "#9aa0a6" }}>No drawing open.</div>;
  }
  const pick = sel.length === 1 ? sel[0] : null;
  const layer = pick ? dl.layers.find((l) => l.name === pick.layer) : null;
  const kinds = new Set(sel.map((p) => p.kind));
  // ⚠ AutoCAD's own wording for the drop-down at the top of the palette: the TYPE when they
  // agree, otherwise "All (n)". It is the one place that tells you how many you have got.
  const title = !sel.length ? `${drawing?.name ?? "Drawing"} — no selection`
    : sel.length === 1 ? sel[0].kind
    : kinds.size === 1 ? `${[...kinds][0]} (${sel.length})`
    : `All (${sel.length})`;
  return (
    <div style={{ font: "11.5px system-ui", overflow: "auto", height: "100%" }}>
      <div style={{ padding: "5px 8px", background: "#4a4d4f", borderBottom: "1px solid #5d6164",
                    font: "600 11.5px system-ui" }}>
        {title}
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", padding: 4 }}>
        <tbody>
          {sel.length ? (
            <>
              <Section title="General" />
              {row("Type", varies(common(sel, (p) => p.kind)) ?? sel[0].kind)}
              {/* ⚠ The value shown when the selection DISAGREES is "*VARIES*", and picking a
                  new one sets it on all of them — AutoCAD's own behaviour, and the reason the
                  palette is how a drafter fixes forty objects at once. */}
              {row("Layer", onSet
                ? <Choice testid="prop-layer" value={mixed(sel, (p) => p.layer)}
                          options={dl.layers.map((l) => [l.name, l.name])}
                          onPick={(v) => onSet({ layer: v })} />
                : (varies(common(sel, (p) => p.layer)) ?? sel[0].layer))}
              {row("Color", onSet
                ? <ColorCell sel={sel} dl={dl} onPick={(aci) => onSet({ aci })} />
                : (varies(common(sel, (p) => p.aci))
                   ?? <Swatch aci={sel[0].aci}
                              rgb={dl.layers.find((l) => l.name === sel[0].layer)?.rgb ?? null}
                              layerAci={dl.layers.find(
                                (l) => l.name === sel[0].layer)?.aci ?? 7} />))}
              {row("Linetype", onSet
                ? <Choice testid="prop-linetype" value={null}
                          unknown="pdcad does not carry each object's linetype yet — pick one to set it"
                          options={[["BYLAYER", "ByLayer"], ["BYBLOCK", "ByBlock"],
                                    ...Object.keys(LTYPE_DASH).map(
                                      (n) => [n.toUpperCase(), n] as [string, string])]}
                          onPick={(v) => onSet({ linetype: v })} />
                : "ByLayer")}
              {row("Lineweight", onSet
                ? <Choice testid="prop-lw" value={null}
                          unknown="pdcad does not carry each object's lineweight yet — pick one to set it"
                          options={[["-1", "ByLayer"], ["-2", "ByBlock"], ["-3", "Default"],
                                    ...LINEWEIGHTS.map(
                                      (m) => [String(Math.round(m * 100)),
                                              `${m.toFixed(2)} mm`] as [string, string])]}
                          onPick={(v) => onSet({ lineweight: Number(v) })} />
                : lw(layer?.lw))}
              {/* ⚠ Only when the selection IS text. AutoCAD greys the Contents field for a wall
                  rather than hiding it; hiding it is the smaller lie here, because a row that
                  appears and disappears is at least never editable-looking and inert. */}
              {onSet && sel.every((p) => TEXTY.has(p.kind)) && row("Contents",
                <TextCell sel={sel} dl={dl} onSet={(t) => onSet({ text: t })} />)}
              {/* ⚠ The handle is shown because it is the ONE identifier that survives export and
                  re-import, and it is what a bug report needs. AutoCAD shows it too. */}
              {/* ⚠⚠ **ATTRIBUTES ARE HOW THIS OFFICE LABELS EVERYTHING** — ATTDEF in 43.5% of
                  its drawings, ATTRIB in 47.6% — and until now they drew and could not be read
                  or changed. One row per TAG shared by the whole selection, so renaming forty
                  room tags is one edit. */}
              {attTags(sel, dl).map(([tag, val]) => (
                <tr key={`att-${tag}`}>
                  <td style={{ ...L, paddingLeft: 14 }}>{tag}</td>
                  <td style={V}>
                    {onAttr
                      ? <AttrCell tag={tag} value={val}
                                  onSet={(v) => onAttr({ [tag]: v })} />
                      : (val ?? <i style={{ color: "#8a8f94" }}>*VARIES*</i>)}
                  </td>
                </tr>))}
              {/* ⚠⚠ **AN UNDERLAY IS PICKABLE AND SHOWED NOTHING ABOUT ITSELF.** Its frame went
                  into `polys` so it could be selected — and then the palette had no more to say
                  about a traced PDF than about a line. The page, the file and the fade are the
                  three things a drafter actually asks it, and the fade is the one they change.
                  ⚠ Read off the `images` channel rather than fetched: the row already carries
                  every one of them, and a second query would be a second source of truth. */}
              {pick && underlayOf(pick, dl) && (() => {
                const u = underlayOf(pick, dl)!;
                return (<>
                  <Section title="Underlay" />
                  {row("File", u[10] || <i style={{ color: "#8a8f94" }}>not resolved</i>)}
                  {row("Fade", onUnderlay
                    ? <FadeCell value={u[12]} onSet={(v) => onUnderlay({ fade: v })} />
                    : `${u[12]}%`)}
                  {row("Monochrome", onUnderlay
                    ? <Choice testid="prop-ul-mono" value={u[13] ? "1" : "0"}
                              options={[["0", "No"], ["1", "Yes"]]}
                              onPick={(v) => onUnderlay({ mono: v === "1" })} />
                    : (u[13] ? "Yes" : "No"))}
                  {row("Clipped", u[15]?.length ? "Yes" : "No")}
                  {/* ⚠ The frame's own size in DRAWING units — what a drafter checks when the
                      traced dimensions come out at the wrong scale. */}
                  {row("Page size", `${Math.abs(u[3] - u[1]).toFixed(1)} × `
                                    + `${Math.abs(u[8] - u[2]).toFixed(1)}`)}
                </>);
              })()}
              {pick ? row("Handle", <code style={{ fontSize: 11 }}>{pick.handle}</code>)
                    : row("Objects", sel.length)}
            </>
          ) : (
            <>
              {/* ⚠ AutoCAD's own no-selection behaviour: the DRAWING's properties, not a blank. */}
              <Section title="Drawing" />
              {row("Name", drawing?.name ?? "—")}
              {row("Source", drawing?.source_name ?? "—")}
              {row("DXF version", drawing?.dxfversion ?? "—")}
              <Section title="Contents" />
              {row("Objects", dl.counts.entities.toLocaleString())}
              {row("Layers", dl.layers.length)}
              {row("Polylines", dl.counts.polys.toLocaleString())}
              {row("Text", dl.counts.texts.toLocaleString())}
              {row("Points", dl.counts.points.toLocaleString())}
              {/* ⚠⚠ **A COUNT, BECAUSE THE ALTERNATIVE IS A DRAFTER LOOKING FOR SOMETHING THAT
                  IS THERE AND INVISIBLE.** An ACIS entity round-trips perfectly and cannot be
                  drawn — its geometry is a SAT blob, and ezdxf's own parser refuses AutoCAD's.
                  Measured before deciding: REGION is in 20.5% of this office's drawings and is
                  a MEDIAN 0.01% of their drawable content, so this line is the right size of
                  answer and a SAT parser is not. */}
              {!!dl.counts.carried && row("Carried, not drawn",
                Object.entries(dl.carried_by_kind ?? {})
                  .sort((a, b) => b[1] - a[1])
                  .map(([k, n]) => `${n.toLocaleString()} ${k}`).join(", "))}
              <Section title="Extents" />
              {row("Min X,Y", `${dl.extents[0].toFixed(1)}, ${dl.extents[1].toFixed(1)}`)}
              {row("Max X,Y", `${dl.extents[2].toFixed(1)}, ${dl.extents[3].toFixed(1)}`)}
            </>
          )}
        </tbody>
      </table>
      {!sel.length && (
        <div style={{ padding: "8px 8px 12px", color: "#8a8f94", fontSize: 11, lineHeight: 1.6 }}>
          Click an object to select it. Click more to add them.<br />
          Drag <b style={{ color: "#5a9aff" }}>right</b> for a window · drag{" "}
          <b style={{ color: "#5ec85e" }}>left</b> to cross.
        </div>
      )}
    </div>
  );
}

/** ⚠ AutoCAD's ladder is in millimetres and `-1` means ByLayer, `-3` Default. Printing the raw
 *  integer would put "-3" in a column of widths. */
function lw(v: number | undefined): string {
  if (v === undefined || v === -1) return "ByLayer";
  if (v === -3) return "Default";
  if (v === -2) return "ByBlock";
  return `${(v / 100).toFixed(2)} mm`;
}

/** AutoCAD's Select Color dialog, Index Color tab.
 *
 * ⚠⚠ **255 SWATCHES, NOT A COLOUR WHEEL, AND THAT IS NOT NOSTALGIA.** This office's pen table
 * maps ACI NUMBERS to plotted line weights — colour 1 plots at 0.13 mm, colour 5 at 0.30 — so
 * "the colour" of a layer is really an entry in a printing table that a hex picker cannot
 * address. Picking `#ff2200` would store a true colour and the plot would come out with every
 * one of those lines at the default weight.
 *
 * ⚠ The first row is 1–9, the block below is 10–249 in AutoCAD's own 24-wide arrangement, and
 * 250–255 are the greys. Same layout a drafter has been clicking for twenty years.
 */
function ColorGrid({ value, onPick, onClose }: {
  value: number; onPick: (aci: number) => void; onClose: () => void;
}) {
  const cell = (aci: number) => (
    <span key={aci} title={`Color ${aci}`} onClick={() => { onPick(aci); onClose(); }}
          style={{ width: 12, height: 12, display: "inline-block", cursor: "pointer",
                   background: aciToCss(aci, aci, null, true),
                   outline: aci === value ? "2px solid #fff" : "1px solid #2b2d2f",
                   outlineOffset: aci === value ? -1 : 0 }} />
  );
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 120, background: "rgba(0,0,0,.4)",
                  display: "grid", placeItems: "center" }}
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: "#3c3f41", border: "1px solid #555", borderRadius: 4,
                    padding: 10, boxShadow: "0 12px 40px rgba(0,0,0,.6)" }}>
        <div style={{ font: "600 12px system-ui", marginBottom: 8 }}>Select Color</div>
        <div style={{ display: "flex", gap: 2, marginBottom: 8 }}>
          {Array.from({ length: 9 }, (_, i) => cell(i + 1))}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(24, 12px)", gap: 1 }}>
          {Array.from({ length: 240 }, (_, i) => cell(i + 10))}
        </div>
        <div style={{ display: "flex", gap: 2, marginTop: 8 }}>
          {Array.from({ length: 6 }, (_, i) => cell(i + 250))}
        </div>
      </div>
    </div>
  );
}

/** ⚠ AutoCAD's ladder is in millimetres and stored as an INT in 1/100 mm — see `models.Layer`.
 *  Sending 0.35 would arrive as `int(0.35) == 0`, the thinnest line a plotter can draw, which is
 *  how the old app shipped a DXF whose every override was hairline. */
function LineweightPicker({ value, onPick }: { value: number; onPick: (lw: number) => void }) {
  return (
    <select value={value} onChange={(e) => onPick(Number(e.target.value))}
            onClick={(e) => e.stopPropagation()}
            style={{ background: "#2b2d2f", color: "#e8e8e8", border: "1px solid #555",
                     font: "10.5px system-ui", padding: 0, width: 62 }}>
      <option value={-3}>Default</option>
      <option value={-1}>ByLayer</option>
      {LINEWEIGHTS.map((mm) => (
        <option key={mm} value={Math.round(mm * 100)}>{mm.toFixed(2)} mm</option>
      ))}
    </select>
  );
}

export function Layers({ dl, rows, onPatch, vpFreeze, onVpFreeze }: {
  dl: DisplayList | null;
  /** The DOCUMENT's layer table — carries the id a PATCH needs. */
  rows: LayerRow[];
  onPatch: (name: string, patch: Partial<DisplayLayer & LayerRow>) => void;
  /** ⚠⚠ **VP FREEZE, WHICH IS A DIFFERENT COLUMN FROM FREEZE AND A DRAFTER WILL TELL YOU SO.**
   *  ❄ takes a layer off the whole drawing; this takes it off ONE WINDOW. It is how one model
   *  becomes six sheets, and 254 of the 1,803 viewports stored here use it. The column only
   *  appears when exactly one viewport is selected, because "in which window?" has no answer
   *  otherwise — the same reason AutoCAD greys its own VP columns in model space.
   *  UPPERCASE names, because that is how `_sheet_of` reports the frozen set. */
  vpFreeze?: Set<string> | null;
  onVpFreeze?: (name: string, freeze: boolean) => void;
}) {
  const [picking, setPicking] = useState<string | null>(null);
  if (!dl) return <div style={{ padding: 10, color: "#9aa0a6" }}>No drawing open.</div>;
  const vp = vpFreeze && onVpFreeze ? vpFreeze : null;
  const byName = new Map(rows.map((r) => [r.name, r]));
  const open = picking ? dl.layers.find((l) => l.name === picking) : null;
  return (
    <div style={{ font: "11.5px system-ui", overflow: "auto", height: "100%" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ position: "sticky", top: 0, background: "#4a4d4f", textAlign: "left" }}>
            <th style={{ padding: "3px 6px", width: 22 }} title="On / off">◉</th>
            <th style={{ padding: "3px 6px", width: 22 }} title="Freeze">❄</th>
            <th style={{ padding: "3px 6px", width: 22 }} title="Lock">🔒</th>
            {vp && (
              <th style={{ padding: "3px 6px", width: 24 }}
                  title="VP Freeze — frozen in THIS viewport only">▣❄</th>
            )}
            <th style={{ padding: "3px 4px", width: 22 }} title="Color">◧</th>
            {/* ⚠ The palette is ~255 px wide and AutoCAD's Layer Properties Manager is a wide
                floating dialog. Six columns only fit if the NAME gives way — so it truncates
                with the full name on hover, which is what AutoCAD's own narrow docked state
                does. The first version pushed Lineweight clean off the right edge and the
                control simply was not there. */}
            <th style={{ padding: "3px 4px" }}>Name</th>
            <th style={{ padding: "3px 2px", width: 64 }} title="Lineweight">LW</th>
          </tr>
        </thead>
        <tbody>
          {dl.layers.map((l) => {
            const row = byName.get(l.name);
            return (
            <tr key={l.name} style={{ borderBottom: "1px solid #3a3d3f" }}>
              {/* ⚠ A layer turned off is not greyed — it is NOT DRAWN, which is what AutoCAD
                  does and what makes the toggle worth having on a busy drawing. */}
              <td style={{ padding: "1px 6px", cursor: "pointer", textAlign: "center" }}
                  onClick={() => onPatch(l.name, { on: !l.on })}
                  title={l.on ? "On" : "Off"}>
                <span style={{ color: l.on ? "#ffd24a" : "#6a6e72" }}>{l.on ? "☀" : "☾"}</span>
              </td>
              <td style={{ padding: "1px 6px", cursor: "pointer", textAlign: "center" }}
                  onClick={() => onPatch(l.name, { frozen: !l.frozen })}
                  title={l.frozen ? "Frozen" : "Thawed"}>
                <span style={{ color: l.frozen ? "#7fd4ff" : "#6a6e72" }}>
                  {l.frozen ? "❄" : "☼"}
                </span>
              </td>
              {vp && (
                // ⚠ Per VIEWPORT. `LAYFRZ` on a sheet set takes the layer off every sheet at
                // once; this takes it off the one window that is selected, which is what the
                // whole command exists for.
                <td style={{ padding: "1px 6px", cursor: "pointer", textAlign: "center" }}
                    onClick={() => onVpFreeze!(l.name, !vp.has(l.name.toUpperCase()))}
                    title={vp.has(l.name.toUpperCase())
                           ? "Frozen in this viewport — click to thaw it here"
                           : "Visible in this viewport — click to freeze it here only"}>
                  <span style={{ color: vp.has(l.name.toUpperCase()) ? "#ff9f6a" : "#6a6e72" }}>
                    {vp.has(l.name.toUpperCase()) ? "▣" : "▢"}
                  </span>
                </td>
              )}
              {/* ⚠ LOCKED is not OFF. A locked layer still draws and still plots; you simply
                  cannot select what is on it. Merging the two is a support call nobody can
                  reproduce, because the drawing looks exactly right. */}
              <td style={{ padding: "1px 4px", cursor: "pointer", textAlign: "center" }}
                  onClick={() => row && onPatch(l.name, { locked: !row.locked })}
                  title={row?.locked ? "Locked" : "Unlocked"}>
                <span style={{ color: row?.locked ? "#ffb36a" : "#6a6e72" }}>
                  {row?.locked ? "▣" : "▢"}
                </span>
              </td>
              <td style={{ padding: "1px 4px" }}>
                <span onClick={() => setPicking(l.name)} title="Select Color"
                      style={{ display: "inline-block", width: 11, height: 11, cursor: "pointer",
                               border: "1px solid #777",
                               background: l.rgb != null ? rgbToCss(l.rgb)
                                                         : aciToCss(l.aci, l.aci, null, true) }} />
              </td>
              <td title={l.name}
                  style={{ padding: "1px 4px", maxWidth: 84, overflow: "hidden",
                           textOverflow: "ellipsis", whiteSpace: "nowrap",
                           color: l.on && !l.frozen ? "#e8e8e8" : "#7a7e82" }}>
                {l.name}
              </td>
              <td style={{ padding: "1px 2px" }}>
                <LineweightPicker value={l.lw}
                                  onPick={(lw) => onPatch(l.name, { lw })} />
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
      <div style={{ padding: "6px 8px", color: "#8a8f94", fontSize: 11 }}>
        {dl.layers.length} layers · {dl.layers.filter((l) => l.on && !l.frozen).length} visible
      </div>
      {open && (
        <ColorGrid value={open.aci} onClose={() => setPicking(null)}
                   onPick={(aci) => onPatch(open.name, {
                     aci,
                     // ⚠⚠ **PICKING AN INDEX COLOUR CLEARS THE TRUE COLOUR.** They are two
                     // fields and `true_colour` OVERRIDES `aci` — in `display.py`, in
                     // `dxfio.export_dxf` and in AutoCAD. Setting the index and leaving an old
                     // RGB behind gives a layer whose swatch says 3 and whose lines are the
                     // colour someone chose last year.
                     rgb: null, true_colour: null,
                   })} />
      )}
    </div>
  );
}

/** The entity kinds whose string can be edited in place. ⚠ ATTRIB and ATTDEF are here because a
 *  title block's fields ARE attributes — editing the sheet number is the commonest text edit in
 *  a real drawing, and it is not a TEXT. */
const TEXTY = new Set(["TEXT", "MTEXT", "ATTRIB", "ATTDEF"]);

/** What a property reads as across a multiple selection: the shared value, or null for mixed. */
function mixed(sel: Pick[], f: (p: Pick) => string): string | null {
  const first = f(sel[0]);
  return sel.every((p) => f(p) === first) ? first : null;
}

/** A property drop-down. ⚠ `value === null` means the selection disagrees, and the control shows
 *  AutoCAD's `*VARIES*` — an empty box would read as "no value", which is a different fact. */
function Choice({ value, options, onPick, testid, unknown }: {
  value: string | null;
  options: [string, string][];
  onPick: (v: string) => void;
  testid: string;
  /** ⚠⚠ **"WE DO NOT KNOW" IS NOT "THEY DISAGREE", AND PRINTING ONE FOR THE OTHER IS A LIE THE
   *  DRAFTER CANNOT SEE THROUGH.** The display list carries each object's LAYER and COLOUR and
   *  does not yet carry its linetype or lineweight — so those two fields showed `*VARIES*` on a
   *  selection of twelve identical objects. `*VARIES*` is a statement about the drawing;
   *  this is a statement about the app. */
  unknown?: string;
}) {
  const tag = unknown ? "—" : "*VARIES*";
  return (
    <select data-testid={testid} value={value ?? "__none__"}
            title={value === null && unknown ? unknown : ""}
            onChange={(e) => { if (e.target.value !== "__none__") onPick(e.target.value); }}
            style={{ width: "100%", background: "#2b2d2f",
                     color: value === null && unknown ? "#9aa0a6" : "#e8e8e8",
                     border: "1px solid #4a4d4f", font: "11.5px system-ui", padding: "1px 3px" }}>
      {value === null && <option value="__none__">{tag}</option>}
      {options.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
    </select>);
}

/** The colour swatch, clickable. ⚠ It opens the 255-index grid, not a hex picker — see
 *  `ColorGrid`: this office's pen table maps ACI NUMBERS to plotted line weights. */
function ColorCell({ sel, dl, onPick }: {
  sel: Pick[]; dl: DisplayList; onPick: (aci: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const all = sel.every((p) => p.aci === sel[0].aci);
  const ly = dl.layers.find((l) => l.name === sel[0].layer);
  return (
    <>
      <span data-testid="prop-color" onClick={() => setOpen(true)}
            style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
        {all ? <Swatch aci={sel[0].aci} rgb={ly?.rgb ?? null} layerAci={ly?.aci ?? 7} />
             : <span style={{ color: "#c8a35a" }}>*VARIES*</span>}
      </span>
      {open && <ColorGrid value={sel[0].aci} onClose={() => setOpen(false)}
                          onPick={(aci) => onPick(aci)} />}
    </>);
}

/** The string of a TEXT or MTEXT, edited in place.
 *
 * ⚠⚠ **COMMITTED ON BLUR OR ENTER, NOT ON EVERY KEYSTROKE.** Each write is a server round trip
 * AND an undo-stack entry; typing "GROUND FLOOR PLAN" one character at a time would put
 * seventeen entries on the stack and make Ctrl+Z useless for the next minute.
 *
 * ⚠ The value comes from the DISPLAY LIST, not from the pick — a `Pick` carries the handle,
 * kind, layer and colour, and never the text. Looking it up here means the field shows what is
 * actually drawn rather than a copy that can drift.
 */
/** The attribute tags shared by every selected block reference, with the value where they all
 *  agree and `null` where they do not.
 *
 *  ⚠⚠ **THE INTERSECTION, NOT THE UNION.** Offering a tag that only half the selection has
 *  would write it to the half that does and silently skip the rest, reporting success. AutoCAD
 *  shows the same: select two different blocks and the attribute rows disappear.
 */
function attTags(sel: Pick[], dl: DisplayList): [string, string | null][] {
  const lists = sel.map((p) => dl.atts?.[p.handle]).filter(Boolean) as [string, string][][];
  if (!lists.length || lists.length !== sel.length) return [];
  const first = lists[0];
  const out: [string, string | null][] = [];
  for (const [tag] of first) {
    const vals = lists.map((l) => l.find(([t]) => t === tag)?.[1]);
    if (vals.some((v) => v === undefined)) continue;      // not on every one of them
    out.push([tag, vals.every((v) => v === vals[0]) ? (vals[0] as string) : null]);
  }
  return out;
}

/** One attribute value, edited in place. ⚠ Same shape as `TextCell` on purpose — a drafter
 *  should not have to learn two ways to type a string into this palette. */
function AttrCell({ tag, value, onSet }: {
  tag: string; value: string | null; onSet: (v: string) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const shown = draft ?? value ?? "";
  const commit = () => {
    if (draft !== null && draft !== value) onSet(draft);
    setDraft(null);
  };
  return (
    <input data-testid={`prop-att-${tag}`} value={shown}
           placeholder={value === null ? "*VARIES*" : ""}
           onChange={(e) => setDraft(e.target.value)}
           onBlur={commit}
           onKeyDown={(e) => {
             if (e.key === "Enter") { e.preventDefault(); commit(); (e.target as HTMLInputElement).blur(); }
             if (e.key === "Escape") { setDraft(null); (e.target as HTMLInputElement).blur(); }
           }}
           style={{ width: "100%", background: "#2b2d2f", color: "#e8e8e8",
                    border: "1px solid #4a4d4f", font: "11.5px system-ui", padding: "1px 3px" }} />);
}

function TextCell({ sel, dl, onSet }: {
  sel: Pick[]; dl: DisplayList; onSet: (t: string) => void;
}) {
  const rows = dl.texts.filter((t) => sel.some((p) => p.handle === t[7]));
  const current = rows.length && rows.every((t) => t[6] === rows[0][6]) ? rows[0][6] : null;
  const [draft, setDraft] = useState<string | null>(null);
  const shown = draft ?? current ?? "";
  const commit = () => {
    if (draft !== null && draft !== current) onSet(draft);
    setDraft(null);
  };
  return (
    <input data-testid="prop-text" value={shown}
           placeholder={current === null ? "*VARIES*" : ""}
           onChange={(e) => setDraft(e.target.value)}
           onBlur={commit}
           onKeyDown={(e) => {
             if (e.key === "Enter") { e.preventDefault(); commit(); (e.target as HTMLInputElement).blur(); }
             if (e.key === "Escape") { setDraft(null); (e.target as HTMLInputElement).blur(); }
           }}
           style={{ width: "100%", background: "#2b2d2f", color: "#e8e8e8",
                    border: "1px solid #4a4d4f", font: "11.5px system-ui", padding: "1px 3px" }} />);
}
